import classNames from 'classnames/bind';
import { Dispatch, SetStateAction, useState } from 'react';
import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import {
  IPassengers,
  SeatClass,
  TripType,
  getSeatClassMap,
} from 'contexts/searchParams';
import styles from '../index.module.scss';
import SelectSeatOption from './selectSeatOption';

const cx = classNames.bind(styles);
const MAXIMUM_PEOPLE = 9;

interface IProps {
  isDomestic: boolean;
  passengers: IPassengers;
  seatClass: SeatClass;
  tripType: TripType;
  setPassengers: (personType: string, diff: number) => void;
  setSeatClass: (seatClass: SeatClass) => void;
  toggleSeatOption: Dispatch<SetStateAction<boolean>>;
}

enum Alert {
  ADULT_PER_INFANT = 1,
  ADULT_REQUIRED = 2,
  MAX_PEOPLE = 3,
  MIN_ADULT = 4,
}

const messages = new Map([
  [Alert.ADULT_PER_INFANT, '유아 1명당 성인 1명 동반 시 조회 가능합니다.'],
  [
    Alert.ADULT_REQUIRED,
    '소아/유아 항공권은 동반 성인 포함 시 조회 가능합니다.',
  ],
  [
    Alert.MAX_PEOPLE,
    `전체 탑승객은 총 ${MAXIMUM_PEOPLE}명까지 조회 가능합니다.`,
  ],
  [Alert.MIN_ADULT, '성인은 최소 1명부터 조회할 수 있습니다.'],
]);

const SelectSeat = ({
  isDomestic,
  passengers,
  seatClass,
  tripType,
  setPassengers,
  setSeatClass,
  toggleSeatOption,
}: IProps) => {
  const [alert, setAlert] = useState<Alert>();
  const ref = useOffClick(e => {
    e.stopPropagation();
    toggleSeatOption(false);
  });
  const { adult, child, infant } = passengers;
  const seatClasses = Array.from(getSeatClassMap(isDomestic));
  const remains = MAXIMUM_PEOPLE - adult - child - infant;
  const componentMap = [
    {
      personType: 'adult',
      title: '성인',
      value: adult,
      limitText: `만 ${isDomestic ? 13 : 12}세 이상`,
      min: Math.max(1, infant),
      max: remains + adult,
    },
    {
      personType: 'child',
      title: '소아',
      value: child,
      limitText: `만 ${isDomestic ? 13 : 12}세 미만`,
      min: 0,
      max: remains + child,
    },
    {
      personType: 'infant',
      title: '유아',
      value: infant,
      limitText: '24개월 미만',
      min: 0,
      max: Math.min(remains + infant, adult),
    },
  ];

  const handleSelectPassengers = (
    personType: string,
    diff: number,
    valid: boolean,
  ) => {
    const alt =
      personType === 'adult' && adult + diff < 1
        ? child + infant > 0
          ? Alert.ADULT_REQUIRED
          : Alert.MIN_ADULT
        : diff > remains
          ? Alert.MAX_PEOPLE
          : (personType === 'adult' && adult + diff < infant) ||
              (personType === 'infant' && infant + diff > adult)
            ? Alert.ADULT_PER_INFANT
            : undefined;
    setAlert(alt);
    if (valid) setPassengers(personType, diff);
  };

  return (
    <div className={cx('SelectSeat', 'select_people_fare')} ref={ref}>
      <div className={cx('inner')}>
        <div className={cx('options')}>
          {componentMap.map(
            ({ personType, title, value, limitText, max, min }) => (
              <SelectSeatOption
                key={`seat_${personType}`}
                onSelect={handleSelectPassengers}
                personType={personType}
                title={title}
                value={value}
                limitText={limitText}
                max={max}
                min={min}
              />
            ),
          )}
        </div>
        <div className={cx('options')}>
          {seatClasses.map(([code, name]) => (
            <button
              key={code}
              type="button"
              className={cx('option', 'as_seat', {'is_domestic': isDomestic})}
              aria-checked={code === seatClass}
              onClick={() => setSeatClass(code)}
            >
              {name}
            </button>
          ))}
        </div>
        {alert && <div className={cx('alert')}>{messages.get(alert)}</div>}
      </div>
    </div>
  );
};

export default SelectSeat;
